<div class="page-shell">
  <mat-toolbar class="header">
    <div class="header-title">
      <button mat-icon-button type="button" (click)="back()" [attr.aria-label]="'cancel' | translate">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <span>{{ "clientDetail.title" | translate }}: {{ client?.domain || ("loading" | translate) }}</span>
    </div>
    <div class="actions" *ngIf="form && !loading">
      <button
        mat-raised-button
        color="primary"
        type="button"
        (click)="save()"
        [disabled]="saving || (isDomainChanged && !form.get('domainChangeAcknowledged')?.value)"
      >
        <mat-icon *ngIf="!saving">save</mat-icon>
        <mat-spinner diameter="20" class="dc-btn-spinner" *ngIf="saving"></mat-spinner>
        <span *ngIf="!saving">{{ "save" | translate }}</span>
      </button>
    </div>
  </mat-toolbar>

  <div class="detail-wrapper">
    <div class="detail-layout" *ngIf="form && client">
      <section class="detail-panel">
        <header class="detail-panel__header">
          <h2>{{ "clientDetail.settings" | translate }}</h2>
        </header>
        <form [formGroup]="form" class="form" (ngSubmit)="save()">
          <div class="form-field">
            <label class="field-label">{{ "clientDetail.clientId" | translate }}</label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <input type="text" matInput [value]="client.clientId" readonly />
              <button
                mat-icon-button
                matSuffix
                type="button"
                class="client-id-copy-btn"
                (click)="copyClientId()"
                [attr.aria-label]="'clientDetail.copyClientId' | translate"
              >
                <mat-icon>content_copy</mat-icon>
              </button>
            </mat-form-field>
          </div>

          <div class="form-field">
            <label class="field-label">{{ "client.domain" | translate }}</label>
            <mat-form-field appearance="outline">
              <input type="text" matInput formControlName="domain" [errorStateMatcher]="errorStateMatcher" />
            </mat-form-field>
            <mat-checkbox
              *ngIf="isDomainChanged"
              formControlName="domainChangeAcknowledged"
              class="change-domain-checkbox"
              color="primary"
            >
              {{ "editClient.changeDomainWarning" | translate }}
            </mat-checkbox>
          </div>

          <div class="form-field">
            <label class="field-label">{{ "client.googleAdminEmail" | translate }}</label>
            <mat-form-field appearance="outline">
              <input type="text" matInput formControlName="googleAdminEmail" [errorStateMatcher]="errorStateMatcher" />
            </mat-form-field>
          </div>

          <div class="form-field">
            <label class="field-label">{{ "editClient.subscription" | translate }}</label>
            <mat-form-field appearance="outline">
              <mat-select formControlName="planId" [errorStateMatcher]="errorStateMatcher">
                <mat-option *ngFor="let subscription of subscriptions" [value]="subscription">{{ subscription }}</mat-option>
              </mat-select>
              <mat-error *ngIf="form.hasError('required', ['planId'])">{{ "fieldRequired" | translate }}</mat-error>
            </mat-form-field>
          </div>

          <div class="form-field">
            <label class="field-label">{{ "client.language" | translate }}</label>
            <mat-form-field appearance="outline">
              <mat-select formControlName="language" [errorStateMatcher]="errorStateMatcher">
                <mat-option value="en">EN</mat-option>
                <mat-option value="es">ES</mat-option>
                <mat-option value="pl">PL</mat-option>
              </mat-select>
              <mat-error *ngIf="form.hasError('required', ['language'])">{{ "fieldRequired" | translate }}</mat-error>
            </mat-form-field>
          </div>

          <div class="form-field" *ngIf="form.get('planId')?.value === 'free'">
            <label class="field-label">{{ "client.trialFinishDate" | translate }}</label>
            <mat-form-field appearance="outline">
              <input matInput [matDatepicker]="picker" formControlName="trialFinishDate" [errorStateMatcher]="errorStateMatcher" />
              <mat-hint>YYYY-MM-DD</mat-hint>
              <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
              <mat-error *ngIf="form.hasError('required', ['trialFinishDate'])">{{ "fieldRequired" | translate }}</mat-error>
              <mat-error *ngIf="form.hasError('dateInvalid', ['trialFinishDate'])">{{ "futureDateInvalid" | translate }}</mat-error>
            </mat-form-field>
          </div>

          <div class="form-field">
            <label class="field-label">{{ "editClient.billingType" | translate }}</label>
            <mat-form-field appearance="outline">
              <mat-select formControlName="billingMode" [errorStateMatcher]="errorStateMatcher">
                <mat-option *ngFor="let mode of billingModes" [value]="mode">
                  {{ ("client.billingType." + mode) | translate }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="form-field">
            <mat-checkbox formControlName="excludedNewEmployees" color="primary">
              {{ "editClient.excludedNewEmployees" | translate }}
            </mat-checkbox>
          </div>

          <section class="optional-interfaces" formGroupName="optionalInterfaces">
            <h3 class="optional-interfaces__title">{{ "editClient.optionalInterfaces.title" | translate }}</h3>
            <p class="optional-interfaces__hint">{{ "editClient.optionalInterfaces.hint" | translate }}</p>

            <div class="optional-interfaces__group">
              <label class="field-label">{{ "editClient.optionalInterfaces.appsTitle" | translate }}</label>
              <mat-checkbox formControlName="mobileApps" color="primary">
                {{ "editClient.optionalInterfaces.mobileApps" | translate }}
              </mat-checkbox>
              <mat-checkbox formControlName="browserExtension" color="primary">
                {{ "editClient.optionalInterfaces.browserExtension" | translate }}
              </mat-checkbox>
            </div>

            <div class="optional-interfaces__group">
              <label class="field-label">{{ "editClient.optionalInterfaces.walletTitle" | translate }}</label>
              <mat-checkbox formControlName="appleWallet" color="primary">
                {{ "editClient.optionalInterfaces.appleWallet" | translate }}
              </mat-checkbox>
              <mat-checkbox formControlName="googleWallet" color="primary">
                {{ "editClient.optionalInterfaces.googleWallet" | translate }}
              </mat-checkbox>
            </div>

            <div class="optional-interfaces__group">
              <label class="field-label">{{ "editClient.optionalInterfaces.brandingTitle" | translate }}</label>
              <mat-checkbox formControlName="emailSignatures" color="primary">
                {{ "editClient.optionalInterfaces.emailSignatures" | translate }}
              </mat-checkbox>
              <mat-checkbox formControlName="virtualBackgrounds" color="primary">
                {{ "editClient.optionalInterfaces.virtualBackgrounds" | translate }}
              </mat-checkbox>
            </div>

            <div class="optional-interfaces__group">
              <label class="field-label">{{ "editClient.optionalInterfaces.linkPreviewTitle" | translate }}</label>
              <mat-radio-group formControlName="linkPreviewMode" class="optional-interfaces__radios">
                <mat-radio-button *ngFor="let mode of linkPreviewModes" [value]="mode" color="primary">
                  {{ ("editClient.optionalInterfaces.linkPreview." + mode) | translate }}
                </mat-radio-button>
              </mat-radio-group>
            </div>

            <div class="optional-interfaces__group">
              <label class="field-label">{{ "editClient.optionalInterfaces.digitalCardTitle" | translate }}</label>
              <mat-checkbox formControlName="digitalCard" color="primary">
                {{ "editClient.optionalInterfaces.digitalCard" | translate }}
              </mat-checkbox>
              <mat-checkbox formControlName="selfPrintCards" color="primary">
                {{ "editClient.optionalInterfaces.selfPrintCards" | translate }}
              </mat-checkbox>
              <mat-checkbox formControlName="contextualQr" color="primary">
                {{ "editClient.optionalInterfaces.contextualQr" | translate }}
              </mat-checkbox>
            </div>

            <div class="optional-interfaces__group">
              <label class="field-label">{{ "editClient.optionalInterfaces.namePronunciationTitle" | translate }}</label>
              <mat-checkbox formControlName="namePronunciation" color="primary">
                {{ "editClient.optionalInterfaces.namePronunciation" | translate }}
              </mat-checkbox>
              <ng-container *ngIf="form.get('optionalInterfaces.namePronunciation')?.value">
                <mat-radio-group formControlName="namePronunciationScope" class="optional-interfaces__radios">
                  <mat-radio-button *ngFor="let scope of namePronunciationScopes" [value]="scope" color="primary">
                    {{ ("editClient.optionalInterfaces.namePronunciationScope." + scope) | translate }}
                  </mat-radio-button>
                </mat-radio-group>
                <mat-form-field
                  appearance="outline"
                  *ngIf="form.get('optionalInterfaces.namePronunciationScope')?.value === 'designated_roles'"
                  class="optional-interfaces__roles"
                >
                  <mat-label>{{ "editClient.optionalInterfaces.namePronunciationRoles" | translate }}</mat-label>
                  <mat-select formControlName="namePronunciationRoles" multiple [errorStateMatcher]="errorStateMatcher">
                    <mat-option *ngFor="let role of namePronunciationRoleOptions" [value]="role">
                      {{ role }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </ng-container>
            </div>
          </section>
        </form>
      </section>

      <aside class="detail-side">
        <section class="detail-panel">
          <header class="detail-panel__header">
            <h2>{{ "clientDetail.notes.title" | translate }}</h2>
          </header>
          <div class="notes-composer">
            <mat-form-field appearance="outline" class="notes-composer__field" subscriptSizing="dynamic">
              <mat-label>{{ "clientDetail.notes.placeholder" | translate }}</mat-label>
              <textarea matInput rows="3" [(ngModel)]="newNoteBody" [ngModelOptions]="{ standalone: true }"></textarea>
            </mat-form-field>
            <button
              mat-stroked-button
              color="primary"
              type="button"
              (click)="addNote()"
              [disabled]="noteSaving || !newNoteBody.trim()"
            >
              <mat-icon>add</mat-icon>
              {{ "clientDetail.notes.add" | translate }}
            </button>
          </div>

          <div class="empty-inline" *ngIf="notes.length === 0">
            {{ "clientDetail.notes.empty" | translate }}
          </div>

          <ul class="notes-list" *ngIf="notes.length > 0">
            <li class="notes-list__item" *ngFor="let note of notes">
              <ng-container *ngIf="editingNoteId !== note.clientNoteId; else editNoteTpl">
                <p class="notes-list__body">{{ note.body }}</p>
                <div class="notes-list__meta">
                  <span>{{ note.createdBy || ("clientDetail.unknownAuthor" | translate) }}</span>
                  <span>{{ note.createdAt | localizedDate : "short" }}</span>
                </div>
                <div class="notes-list__actions">
                  <button mat-button type="button" (click)="startEditNote(note)">{{ "edit" | translate }}</button>
                  <button mat-button color="warn" type="button" (click)="deleteNote(note)">{{ "delete" | translate }}</button>
                </div>
              </ng-container>
              <ng-template #editNoteTpl>
                <mat-form-field appearance="outline" class="notes-composer__field" subscriptSizing="dynamic">
                  <textarea matInput rows="3" [(ngModel)]="noteDraft" [ngModelOptions]="{ standalone: true }"></textarea>
                </mat-form-field>
                <div class="notes-list__actions">
                  <button mat-stroked-button type="button" (click)="cancelEditNote()">{{ "cancel" | translate }}</button>
                  <button
                    mat-raised-button
                    color="primary"
                    type="button"
                    (click)="saveNote(note)"
                    [disabled]="noteSaving || !noteDraft.trim()"
                  >
                    {{ "save" | translate }}
                  </button>
                </div>
              </ng-template>
            </li>
          </ul>
        </section>

        <section class="detail-panel">
          <header class="detail-panel__header detail-panel__header--row">
            <h2>{{ "clientDetail.documents.title" | translate }}</h2>
            <button mat-stroked-button type="button" (click)="openAddDocumentDialog()">
              <mat-icon>upload_file</mat-icon>
              {{ "clientDetail.documents.upload" | translate }}
            </button>
          </header>
          <div class="empty-inline" *ngIf="documents.length === 0">
            {{ "clientDetail.documents.empty" | translate }}
          </div>

          <ul class="docs-list" *ngIf="documents.length > 0">
            <li class="docs-list__item" *ngFor="let doc of documents">
              <div class="docs-list__info">
                <mat-icon>description</mat-icon>
                <div>
                  <div class="docs-list__name">{{ doc.fileName }}</div>
                  <div class="docs-list__meta">
                    {{ formatBytes(doc.sizeBytes) }}
                    · {{ doc.uploadedBy || ("clientDetail.unknownAuthor" | translate) }}
                    · {{ doc.createdAt | localizedDate : "short" }}
                  </div>
                </div>
              </div>
              <div class="docs-list__actions">
                <button mat-icon-button type="button" (click)="downloadDocument(doc)" [attr.aria-label]="'clientDetail.documents.download' | translate">
                  <mat-icon>download</mat-icon>
                </button>
                <button mat-icon-button type="button" color="warn" (click)="deleteDocument(doc)" [attr.aria-label]="'delete' | translate">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </li>
          </ul>
        </section>
      </aside>
    </div>
  </div>

  <div class="dbc1-loading dbc1-loading--overlay" *ngIf="loading">
    <app-loading></app-loading>
  </div>
</div>
